import { useParams, useNavigate } from 'react-router-dom';
import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { API_BASE_URL, getVerification } from '../services/api'; // adjust the path if your api.js is elsewhere
import CalendarMonthGrid from '../components/calendar/CalendarMonthGrid';
import VerificationPanel from '../components/VerificationPanel';

// AYNI Design System
const styles = {
  container: {
    padding: '4px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    minHeight: 'calc(100vh - 40px)',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  headerRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  title: {
    fontSize: '14px',
    fontWeight: '600',
    color: 'var(--text-primary)',
    margin: 0,
    background: 'var(--bg-button-secondary)',
    padding: '8px 16px',
    borderRadius: '8px',
    border: '1px solid var(--border-default)',
  },
  backButton: {
    padding: '8px 16px',
    backgroundColor: 'var(--bg-button-secondary)',
    color: 'var(--text-secondary)',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  exportButton: {
    padding: '0 16px',
    backgroundColor: 'var(--bg-button-secondary)',
    color: 'var(--text-secondary)',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    height: '36px',
    display: 'flex',
    alignItems: 'center',
  },
  addButton: {
    padding: '0',
    backgroundColor: 'var(--color-primary)',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '20px',
    fontWeight: '500',
    cursor: 'pointer',
    width: '36px',
    height: '36px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  viewToggle: {
    display: 'flex',
    backgroundColor: 'var(--bg-button-secondary)',
    borderRadius: '8px',
    padding: '2px',
    gap: '2px',
    height: '36px',
    alignItems: 'center',
  },
  viewToggleBtn: {
    padding: '0 14px',
    border: 'none',
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: '500',
    cursor: 'pointer',
    backgroundColor: 'transparent',
    color: 'var(--text-tertiary)',
    height: '30px',
    display: 'flex',
    alignItems: 'center',
  },
  viewToggleBtnActive: {
    padding: '0 14px',
    border: 'none',
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: '500',
    cursor: 'pointer',
    backgroundColor: 'var(--bg-card)',
    color: 'var(--text-primary)',
    height: '30px',
    display: 'flex',
    alignItems: 'center',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  },
  card: {
    backgroundColor: 'var(--bg-card)',
    borderRadius: '12px',
    padding: '0',
    boxShadow: 'var(--shadow-card)',
    border: '1px solid var(--border-default)',
    overflowX: 'auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  th: {
    padding: '9px 10px',
    textAlign: 'left',
    fontSize: '11px',
    fontWeight: '600',
    color: 'var(--text-muted)',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    borderBottom: '1px solid var(--border-default)',
    backgroundColor: 'var(--bg-table-header)',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  thNoSort: {
    padding: '9px 10px',
    textAlign: 'left',
    fontSize: '11px',
    fontWeight: '600',
    color: 'var(--text-muted)',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    borderBottom: '1px solid var(--border-default)',
    backgroundColor: 'var(--bg-table-header)',
    whiteSpace: 'nowrap',
  },
  td: {
    padding: '9px 10px',
    fontSize: '13px',
    color: 'var(--text-primary)',
    borderBottom: '1px solid var(--border-light)',
    whiteSpace: 'nowrap',
    verticalAlign: 'middle',
  },
  tdCenter: {
    padding: '9px 10px',
    fontSize: '13px',
    color: 'var(--text-primary)',
    borderBottom: '1px solid var(--border-light)',
    textAlign: 'center',
    whiteSpace: 'nowrap',
    verticalAlign: 'middle',
  },
  // Expandable row
  expandableRow: {
    cursor: 'pointer',
    transition: 'background-color 0.15s',
  },
  chevron: {
    fontSize: '12px',
    color: 'var(--text-faint)',
    transition: 'transform 0.2s',
    display: 'inline-block',
  },
  chevronOpen: {
    fontSize: '12px',
    color: 'var(--color-primary)',
    transition: 'transform 0.2s',
    display: 'inline-block',
    transform: 'rotate(90deg)',
  },
  expandedPanel: {
    backgroundColor: 'var(--bg-elevated)',
    borderBottom: '2px solid var(--border-medium)',
  },
  expandedPanelInner: {
    padding: '14px 20px 16px',
    display: 'grid',
    gridTemplateColumns: 'auto auto 1fr',
    gap: '12px 32px',
    alignItems: 'center',
  },
  expandedRow2: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
    gridColumn: '1 / -1',
    paddingTop: '4px',
    borderTop: '1px solid var(--border-default)',
  },
  expandedGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '3px',
  },
  expandedLabel: {
    fontSize: '10px',
    fontWeight: '600',
    color: 'var(--text-muted)',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  expandedValue: {
    fontSize: '13px',
    color: 'var(--text-primary)',
  },
  expandedActions: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  sortIcon: {
    marginLeft: '4px',
    fontSize: '10px',
    color: '#bbb',
  },
  sortIconActive: {
    marginLeft: '4px',
    fontSize: '10px',
    color: 'var(--color-primary)',
  },
  // UID cell
  uidText: {
    fontSize: '13px',
    color: 'var(--text-tertiary)',
    lineHeight: '22px',
  },
  copyIcon: {
    background: 'none',
    border: 'none',
    padding: '2px 4px',
    fontSize: '13px',
    color: 'var(--text-faint)',
    cursor: 'pointer',
    verticalAlign: 'middle',
  },
  // Status badges
  badgeActive: {
    display: 'inline-block',
    padding: '4px 12px',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: '600',
    backgroundColor: 'var(--badge-green-bg)',
    color: 'var(--badge-green-text)',
  },
  badgeFuture: {
    display: 'inline-block',
    padding: '4px 12px',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: '600',
    backgroundColor: 'var(--badge-blue-bg)',
    color: 'var(--badge-blue-text)',
  },
  badgeBlocked: {
    display: 'inline-block',
    padding: '4px 12px',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: '600',
    backgroundColor: 'var(--badge-red-bg)',
    color: 'var(--badge-red-text)',
  },
  badgeInactive: {
    display: 'inline-block',
    padding: '4px 12px',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: '600',
    backgroundColor: 'var(--badge-gray-bg)',
    color: 'var(--badge-gray-text)',
  },
  badgePending: {
    display: 'inline-block',
    padding: '4px 12px',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: '600',
    backgroundColor: '#fef3c7',
    color: '#92400e',
  },
  // Origin badges
  badgeSimulated: {
    display: 'inline-block',
    padding: '4px 10px',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: '600',
    backgroundColor: 'var(--badge-purple-bg)',
    color: 'var(--badge-purple-text)',
    border: '1px solid #d3c6ff',
  },
  badgeReal: {
    display: 'inline-block',
    padding: '4px 10px',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: '600',
    backgroundColor: 'var(--badge-green-bg)',
    color: 'var(--badge-green-text)',
    border: '1px solid #c8e6c9',
  },
  // Remarks
  remarkBadgeWarn: {
    display: 'inline-block',
    padding: '3px 8px',
    borderRadius: '20px',
    fontSize: '11px',
    fontWeight: '700',
    backgroundColor: 'var(--badge-red-bg)',
    color: 'var(--badge-red-text)',
    cursor: 'help',
  },
  remarkBadgeOk: {
    display: 'inline-block',
    padding: '3px 8px',
    borderRadius: '20px',
    fontSize: '11px',
    fontWeight: '600',
    backgroundColor: 'var(--badge-green-bg)',
    color: 'var(--badge-green-text)',
    cursor: 'default',
  },
  // Action button
  actionButton: {
    padding: '6px 12px',
    fontSize: '13px',
    backgroundColor: 'var(--bg-button-secondary)',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    color: 'var(--text-secondary)',
    fontWeight: '500',
    transition: 'background-color 0.2s',
  },
  dropdown: {
    position: 'absolute',
    top: '100%',
    right: '0',
    backgroundColor: 'var(--bg-card)',
    border: '1px solid var(--border-default)',
    borderRadius: '8px',
    boxShadow: 'var(--shadow-dropdown)',
    zIndex: 1000,
    minWidth: '200px',
    marginTop: '4px',
    overflow: 'hidden',
  },
  dropdownItem: {
    width: '100%',
    padding: '10px 16px',
    textAlign: 'left',
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    fontSize: '13px',
    color: 'var(--text-secondary)',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  dropdownDivider: {
    borderBottom: '1px solid var(--border-light)',
  },
  // Test column
  testInput: {
    padding: '6px 8px',
    border: '1px solid var(--border-input)',
    borderRadius: '6px',
    fontSize: '13px',
    outline: 'none',
    color: 'var(--text-primary)',
    backgroundColor: 'var(--bg-input)',
  },
  launchTestButton: {
    marginLeft: '6px',
    padding: '6px 12px',
    fontSize: '12px',
    fontWeight: '500',
    backgroundColor: 'var(--color-primary)',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
  },
  deleteButton: {
    marginLeft: '8px',
    padding: '6px 12px',
    fontSize: '12px',
    fontWeight: '500',
    backgroundColor: 'var(--badge-red-bg)',
    color: 'var(--badge-red-text)',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
  },
  // Past reservations section
  sectionTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: 'var(--text-primary)',
    margin: 0,
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  countBadge: {
    backgroundColor: 'var(--bg-button-secondary)',
    color: 'var(--text-muted)',
    padding: '2px 10px',
    borderRadius: '20px',
    fontSize: '13px',
    fontWeight: '500',
  },
  // Modals
  modalOverlay: {
    position: 'fixed',
    inset: 0,
    background: 'var(--overlay-bg)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 50,
  },
  modalContent: {
    background: 'var(--bg-card)',
    borderRadius: '12px',
    padding: '24px',
    width: '420px',
    boxShadow: 'var(--shadow-modal)',
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
    paddingBottom: '16px',
    borderBottom: '1px solid var(--border-light)',
  },
  modalTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: 'var(--text-primary)',
    margin: 0,
  },
  modalClose: {
    fontSize: '18px',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: 'var(--text-muted)',
    padding: '4px 8px',
    borderRadius: '4px',
  },
  formLabel: {
    fontSize: '11px',
    fontWeight: '600',
    color: 'var(--text-muted)',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: '6px',
    display: 'block',
  },
  formInput: {
    width: '100%',
    padding: '8px 10px',
    border: '1px solid var(--border-input)',
    borderRadius: '6px',
    fontSize: '14px',
    outline: 'none',
    color: 'var(--text-primary)',
    backgroundColor: 'var(--bg-input)',
    boxSizing: 'border-box',
  },
  primaryButton: {
    padding: '10px 20px',
    backgroundColor: 'var(--color-primary)',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
  },
  secondaryButton: {
    padding: '10px 20px',
    backgroundColor: 'var(--bg-button-secondary)',
    color: 'var(--text-secondary)',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
  },
  // Loading
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '60vh',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    color: 'var(--text-muted)',
    fontSize: '16px',
  },
};

const statusBadgeMap = {
  ACTIVE: styles.badgeActive,
  FUTURE: styles.badgeFuture,
  BLOCKED: styles.badgeBlocked,
  INACTIVE: styles.badgeInactive,
};

const PropertyDetails = () => {
  const { propertyId } = useParams();
  const navigate = useNavigate();

  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });
  const [propertyName, setPropertyName] = useState('');
  const [property, setProperty] = useState(null);
  const [strategySaved, setStrategySaved] = useState(false);
  const [testDates, setTestDates] = useState({});
  const [assignedCleaners, setAssignedCleaners] = useState({});
  const [isSimModalOpen, setIsSimModalOpen] = useState(false);

  // Re-dispatch modal state
  const [isCleanerPickerOpen, setIsCleanerPickerOpen] = useState(false);
  const [cleanersForPicker, setCleanersForPicker] = useState([]);
  const [selectedCleanerIds, setSelectedCleanerIds] = useState([]);
  const [currentReservation, setCurrentReservation] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState({});
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [viewMode, setViewMode] = useState('table'); // 'table' | 'calendar'
  const [pendingExtensions, setPendingExtensions] = useState([]);
  const [verifications, setVerifications] = useState({});
  const [activeTab, setActiveTab] = useState('active'); // 'active' | 'pending' | 'past'
  const [dispatchOpen, setDispatchOpen] = useState(false); // gear popup toggle

  // simplified form: check-in, check-out, dispatch only
  const [simForm, setSimForm] = useState({
    check_in: '',
    check_out: '',
    dispatch: true,
  });

  // --- polling controls ---
  const POLL_MS = 15000;
  const timerRef = useRef(null);
  const inFlightRef = useRef(false);
  const isVisibleRef = useRef(true);
  const activeRef = useRef(null);
  const pendingRef = useRef(null);
  const pastRef = useRef(null);

  const fetchReservations = async () => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;

    try {
      const resData = await axios.get(`${API_BASE_URL}/properties/${propertyId}/reservations`);
      let data = resData.data;

      // tag type: treat 'Reserved' and 'Simulated' as guest, everything else as blocked
      data = data.map((r) => ({
        ...r,
        reservation_type:
          r.STATUS === 'Reserved' || r.STATUS === 'Simulated' ? 'guest' : 'blocked',
      }));

      // property name
      const propertiesRes = await axios.get(`${API_BASE_URL}/properties`);
      const properties = propertiesRes.data;
      const prop = properties.find((p) => p.id === parseInt(propertyId, 10));
      setPropertyName(prop?.name || `Property ${propertyId}`);
      setProperty(prop || null);

      // assigned cleaners
      const cleanersRes = await axios.get(`${API_BASE_URL}/assigned-cleaners/${propertyId}`);
      const cleanerData = cleanersRes.data;
      setAssignedCleaners(cleanerData);

      // fetch pending extensions (auto-poll — banner appears without refresh)
      try {
        const extRes = await axios.get(`${API_BASE_URL}/pending-extensions/${propertyId}`);
        setPendingExtensions(extRes.data);
      } catch (_) { /* ignore — non-critical */ }

      // fetch verifications
      try {
        const verRes = await axios.get(`${API_BASE_URL}/verifications/${propertyId}`);
        const verData = verRes.data;
        const verMap = {};
        verData.forEach(v => { verMap[v.reservation_uid] = v; });
        setVerifications(verMap);
      } catch (_) { /* non-critical */ }

      // merge assignments
      const updated = data.map((res) => {
        const matchingKeys = Object.keys(cleanerData).filter((key) =>
          key.startsWith(`${res.UID}__`)
        );
        const reservation_type =
          res.STATUS === 'Reserved' || res.STATUS === 'Simulated' ? 'guest' : 'blocked';

        if (matchingKeys.length > 0) {
          const [, matchedCheckout] = matchingKeys[0].split('__');
          return {
            ...res,
            reservation_type,
            assigned_cleaner: cleanerData[matchingKeys[0]],
            ['CHECK-OUT']: matchedCheckout,
          };
        }

        // Preserve backend's assigned_cleaner if no match found in cleanerData
        return {
          ...res,
          reservation_type,
          assigned_cleaner: res.assigned_cleaner || 'Unassigned'
        };
      });

      setReservations(updated);
    } catch (error) {
      console.error('Failed to fetch reservations:', error);
    } finally {
      inFlightRef.current = false;
      setLoading(false);
    }
  };

  useEffect(() => {
    // run immediately on mount / property change
    fetchReservations();

    const startPolling = () => {
      if (timerRef.current) return; // already polling
      timerRef.current = setInterval(() => {
        if (isVisibleRef.current) fetchReservations();
      }, POLL_MS);
    };

    const stopPolling = () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };

    const onVisibilityChange = () => {
      isVisibleRef.current = document.visibilityState === 'visible';
      if (isVisibleRef.current) {
        // wake-up fetch for snappy UI, then keep polling
        fetchReservations();
        startPolling();
      } else {
        // pause to avoid wasting requests in background tabs
        stopPolling();
      }
    };

    // kick off polling
    startPolling();
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      stopPolling();
    };
  }, [propertyId]);

  const parseDateLocal = (dateStr) => {
    const [y, m, d] = dateStr.split('-');
    return new Date(Number(y), Number(m) - 1, Number(d));
  };

  const getReservationStatus = (checkIn, checkOut, reservationType) => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const co = parseDateLocal(checkOut);
    if (reservationType === 'blocked') {
      return today > co ? 'INACTIVE' : 'BLOCKED';
    }
    const ci = parseDateLocal(checkIn);
    if (today >= ci && today <= co) return 'ACTIVE';
    if (today < ci) return 'FUTURE';
    return 'INACTIVE';
  };

  const handleSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') direction = 'descending';
    setSortConfig({ key, direction });
    const sorted = [...reservations].sort((a, b) => {
      if (a[key] < b[key]) return direction === 'ascending' ? -1 : 1;
      if (a[key] > b[key]) return direction === 'ascending' ? 1 : -1;
      return 0;
    });
    setReservations(sorted);
  };

  const renderSortIcon = (key) => (
    <span style={sortConfig.key === key ? styles.sortIconActive : styles.sortIcon}>
      {sortConfig.key === key
        ? (sortConfig.direction === 'ascending' ? '\u25B2' : '\u25BC')
        : '\u25B2\u25BC'}
    </span>
  );

  const [copiedUid, setCopiedUid] = useState(null);

  const truncateUid = (uid) => {
    if (!uid || uid.length <= 16) return uid;
    return uid.substring(0, 14) + '...';
  };

  const handleCopyUid = async (uid) => {
    try {
      await navigator.clipboard.writeText(uid);
      setCopiedUid(uid);
      setTimeout(() => setCopiedUid(null), 2000);
    } catch {
      // fallback
      const el = document.createElement('textarea');
      el.value = uid;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      setCopiedUid(uid);
      setTimeout(() => setCopiedUid(null), 2000);
    }
  };

  // Origin detector (used by column + CSV)
  const getReservationOrigin = (r) => {
    if (r.Source === 'SIMULATED') return 'Simulated';
    if (r.STATUS === 'Simulated') return 'Simulated';
    if (typeof r.UID === 'string' && r.UID.startsWith('SIM-')) return 'Simulated';
    return 'Real';
  };

  const exportToCSV = () => {
    const headers = [
      'Customer ID', 'Check-in Date', 'Check-out Date',
      'Assigned Aynista', 'Reservation Status', 'Origin'
    ];
    const rows = reservations.map((r) => [
      r.UID,
      r['CHECK-IN'],
      r['CHECK-OUT'],
      r.assigned_cleaner || 'N/A',
      getReservationStatus(r['CHECK-IN'], r['CHECK-OUT'], r.reservation_type),
      getReservationOrigin(r),
    ]);
    const csvContent = [headers, ...rows]
      .map(row => row.map(field => `"${String(field).replace(/"/g, '""')}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `property_${propertyId}_reservations.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleTestDateChange = (uid, date) => {
    setTestDates(prev => ({ ...prev, [uid]: date }));
  };

  const handleLaunchTest = async (uid) => {
    const testDate = testDates[uid];
    if (!testDate) { alert('Please select a test checkout date first.'); return; }
    try {
      const res = await axios.post(`${API_BASE_URL}/simulate-calendar-change`, { property_id: propertyId, new_checkout: testDate, reservation_uid: uid });
      if (res.status === 200) alert('Test triggered!'); else alert('Test failed to trigger');
    } catch (error) {
      console.error('Failed to trigger test:', error);
      alert('Error triggering test');
    }
  };

  const handleReDispatchAll = async (r) => {
    const confirmed = window.confirm(
      `Re-Dispatch will contact ALL cleaners again for this reservation.\n\nProperty: ${propertyName}\nCheck-in: ${r['CHECK-IN']}\nCheck-out: ${r['CHECK-OUT']}\n\nContinue?`
    );

    if (!confirmed) return;

    try {
      const res = await axios.post(`${API_BASE_URL}/re-dispatch`, {
        property_id: propertyId,
        reservation_uid: r.UID,
        check_in: r['CHECK-IN'],
        check_out: r['CHECK-OUT']
      });

      alert(`Re-Dispatch successful!\n\nArchived ${res.data.archived_entries} old log entries.\nDispatching to cleaners now...`);
      fetchReservations();
    } catch (error) {
      console.error('Failed to re-dispatch:', error);
      alert(`Re-Dispatch failed: ${error.response?.data?.error || 'Unknown error'}`);
    }
  };

  const openCleanerPicker = async (r) => {
    setCurrentReservation(r);
    setDropdownOpen({});

    try {
      const res = await axios.post(`${API_BASE_URL}/property/${propertyId}/cleaners-status`, {
        reservation_uid: r.UID,
        check_out: r['CHECK-OUT']
      });

      setCleanersForPicker(res.data);
      setSelectedCleanerIds([]);
      setIsCleanerPickerOpen(true);
    } catch (error) {
      console.error('Failed to load cleaners:', error);
      alert('Failed to load cleaners');
    }
  };

  const handleSelectiveDispatch = async () => {
    if (selectedCleanerIds.length === 0) {
      alert('Please select at least one cleaner');
      return;
    }

    const confirmed = window.confirm(
      `Re-Dispatch to ${selectedCleanerIds.length} selected cleaner(s)?\n\nProperty: ${propertyName}\nCheck-out: ${currentReservation['CHECK-OUT']}\n\nContinue?`
    );

    if (!confirmed) return;

    try {
      const res = await axios.post(`${API_BASE_URL}/re-dispatch`, {
        property_id: propertyId,
        reservation_uid: currentReservation.UID,
        check_in: currentReservation['CHECK-IN'],
        check_out: currentReservation['CHECK-OUT'],
        selected_cleaner_ids: selectedCleanerIds
      });

      alert(`Selective Re-Dispatch successful!\n\nArchived ${res.data.archived_entries} log entries.\nDispatching to ${selectedCleanerIds.length} cleaner(s) now...`);
      setIsCleanerPickerOpen(false);
      fetchReservations();
    } catch (error) {
      console.error('Failed to selective re-dispatch:', error);
      alert(`Re-Dispatch failed: ${error.response?.data?.error || 'Unknown error'}`);
    }
  };

  const toggleCleanerSelection = (cleanerId) => {
    setSelectedCleanerIds(prev =>
      prev.includes(cleanerId)
        ? prev.filter(id => id !== cleanerId)
        : [...prev, cleanerId]
    );
  };

  const toggleDropdown = (uid) => {
    setDropdownOpen(prev => ({ ...prev, [uid]: !prev[uid] }));
  };

  const toggleExpand = (uid) => {
    setExpandedRows(prev => {
      if (prev.has(uid)) return new Set();
      return new Set([uid]);
    });
  };

  const getRemarks = (assignedCleaner) => {
    if (assignedCleaner === 'Unassigned' || assignedCleaner === 'N/A') {
      return { tooltip: 'No cleaner assigned for this reservation', color: '#dc2626' };
    }
    return null;
  };

  // Submit: create simulated reservation on backend
  const handleSimCreate = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        property_id: Number(propertyId),
        check_in: simForm.check_in,
        check_out: simForm.check_out,
        dispatch: !!simForm.dispatch,
      };

      await axios.post(`${API_BASE_URL}/simulate-new-reservation`, payload);

      await fetchReservations();
      setIsSimModalOpen(false);
      setSimForm({ check_in: '', check_out: '', dispatch: true });
    } catch (err) {
      console.error('simulate-new-reservation failed:', err);
      alert('Error creating simulated reservation');
    }
  };

  const handleDeleteSim = async (uid) => {
    if (!uid) return;
    const ok = window.confirm('Delete this simulated reservation?');
    if (!ok) return;

    try {
      await axios.delete(`${API_BASE_URL}/simulate-reservation/${propertyId}/${encodeURIComponent(uid)}`);
      // Refresh table so UI updates
      await fetchReservations();
    } catch (e) {
      console.error('delete simulated reservation failed:', e);
      alert('Error deleting simulated reservation');
    }
  };

  const handleResolveExtension = async (ext, decision) => {
    try {
      await axios.post(`${API_BASE_URL}/resolve-extension`, {
        property_id: ext.property_id,
        new_uid: ext.new_uid,
        decision,
      });
      setPendingExtensions(prev => prev.filter(e => e.new_uid !== ext.new_uid));
    } catch (e) {
      console.error('resolve extension failed:', e);
      alert('Error resolving extension');
    }
  };

  const handleStrategyChange = async (e) => {
    const newStrategy = e.target.value;
    try {
      await axios.put(`${API_BASE_URL}/properties/${propertyId}`, { dispatch_strategy: newStrategy });
      setProperty(prev => ({ ...prev, dispatch_strategy: newStrategy }));
      setStrategySaved(true);
      setTimeout(() => setStrategySaved(false), 2000);
    } catch (err) {
      console.error('Failed to update dispatch strategy:', err);
    }
  };

  // Separate reservations into current/upcoming vs pending cleaning vs past
  const currentAndUpcoming = reservations.filter(r => {
    const status = getReservationStatus(r['CHECK-IN'], r['CHECK-OUT'], r.reservation_type);
    return status !== 'INACTIVE';
  });

  // Pending: checkout passed but cleaning not yet done/verified
  const todayForSplit = new Date(); todayForSplit.setHours(0, 0, 0, 0);
  const threeDaysAgo = new Date(todayForSplit);
  threeDaysAgo.setDate(threeDaysAgo.getDate() - 3);

  const pendingCleaning = reservations.filter(r => {
    const status = getReservationStatus(r['CHECK-IN'], r['CHECK-OUT'], r.reservation_type);
    if (status !== 'INACTIVE') return false;

    const cleaningDateStr = r.cleaning_date || r['CHECK-OUT'];
    const cleanDate = new Date(cleaningDateStr + 'T00:00:00');

    // Case 1: Cleaning date is today or future (rescheduled)
    if (cleanDate >= todayForSplit) return true;

    // Case 2: Recent cleaning (last 3 days) not yet verified
    const ver = verifications[r.UID];
    const isVerified = ver && ['APPROVED', 'ADMIN_APPROVED'].includes(ver.status);
    if (cleanDate >= threeDaysAgo && !isVerified) return true;

    return false;
  }).sort((a, b) => {
    const aDate = a.cleaning_date || a['CHECK-OUT'];
    const bDate = b.cleaning_date || b['CHECK-OUT'];
    return new Date(aDate) - new Date(bDate); // Soonest first
  });

  // Past: all other INACTIVE (verified or old)
  const pendingUIDs = new Set(pendingCleaning.map(p => p.UID));
  const pastReservations = reservations.filter(r => {
    const status = getReservationStatus(r['CHECK-IN'], r['CHECK-OUT'], r.reservation_type);
    return status === 'INACTIVE' && !pendingUIDs.has(r.UID);
  }).sort((a, b) => new Date(b['CHECK-OUT']) - new Date(a['CHECK-OUT'])); // Most recent first

  if (loading) return <div style={styles.loadingContainer}>Loading reservations...</div>;

  // Compute per-tab counts and the primary aynista once, before render
  const activeCount = currentAndUpcoming.length;
  const pendingCount = pendingCleaning.length;
  const pastCount = pastReservations.length;
  const verificationPendingCount = Object.values(verifications || {}).filter(
    v => v && (v.status === 'REVIEW_REQUIRED' || v.pending_review)
  ).length;
  const primaryAynista = (() => {
    const counts = {};
    reservations.forEach(r => {
      const n = r.assigned_cleaner;
      if (n && n !== 'Unassigned') counts[n] = (counts[n] || 0) + 1;
    });
    const entries = Object.entries(counts).sort((a, b) => b[1] - a[1]);
    return entries.length ? entries[0][0] : null;
  })();

  return (
    <div className="ayni-page">
      {/* Editorial back link */}
      <button className="ayni-back" onClick={() => navigate(-1)} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}>
        <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2"><path d="M15 18l-6-6 6-6"/></svg>
        Back to cockpit
      </button>

      {/* Detail hero */}
      <div className="ayni-detail-hero">
        <div className="ayni-hero-left">
          <span className="ayni-eyebrow">
            {property?.city || ''}{property?.city && property?.property_type ? ' · ' : ''}{property?.property_type || ''}
          </span>
          <h1 className="ayni-display-lg" style={{ margin: '12px 0 0' }}>
            {(() => {
              const m = (propertyName || '').match(/^(.*?)(\s+\d+.*)?$/);
              return m && m[2] ? (<>{m[1]}<span className="ayni-italic">{m[2]}</span></>) : propertyName;
            })()}
          </h1>
          <div className="ayni-dmeta">
            {property?.address && (
              <span><span className="ayni-label">Address</span>{property.address}</span>
            )}
            {primaryAynista && (
              <span><span className="ayni-label">Aynista</span><button className="link" type="button">{primaryAynista}</button></span>
            )}
          </div>
        </div>
        <div className="ayni-hero-actions">
          {verificationPendingCount > 0 && (
            <button className="ayni-vchip pending" onClick={() => setActiveTab('pending')}>
              <span className="ayni-num">{verificationPendingCount}</span> verifications pending review
            </button>
          )}
          <div style={{ position: 'relative' }}>
            <button
              className="ayni-gear"
              title="Dispatch settings"
              onClick={() => setDispatchOpen(o => !o)}
            >
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.7 1.7 0 00.3 1.8l.1.1a2 2 0 11-2.8 2.8l-.1-.1a1.7 1.7 0 00-1.8-.3 1.7 1.7 0 00-1 1.5V21a2 2 0 01-4 0v-.1a1.7 1.7 0 00-1-1.5 1.7 1.7 0 00-1.8.3l-.1.1a2 2 0 11-2.8-2.8l.1-.1a1.7 1.7 0 00.3-1.8 1.7 1.7 0 00-1.5-1H3a2 2 0 010-4h.1a1.7 1.7 0 001.5-1 1.7 1.7 0 00-.3-1.8l-.1-.1a2 2 0 112.8-2.8l.1.1a1.7 1.7 0 001.8.3H9a1.7 1.7 0 001-1.5V3a2 2 0 014 0v.1a1.7 1.7 0 001 1.5 1.7 1.7 0 001.8-.3l.1-.1a2 2 0 112.8 2.8l-.1.1a1.7 1.7 0 00-.3 1.8V9a1.7 1.7 0 001.5 1H21a2 2 0 010 4h-.1a1.7 1.7 0 00-1.5 1z"/></svg>
            </button>
            {dispatchOpen && property && (
              <div style={{
                position: 'absolute', top: '40px', right: 0, zIndex: 30,
                background: 'var(--ayni-surface)',
                border: '1px solid var(--ayni-line)',
                padding: '14px 16px', minWidth: '240px',
                boxShadow: '0 8px 24px rgba(0,0,0,0.10)',
              }}>
                <div style={{
                  fontSize: '10px', letterSpacing: '0.12em', textTransform: 'uppercase',
                  color: 'var(--ayni-ink-3)', marginBottom: '8px', fontFamily: 'var(--ayni-f-body)',
                }}>
                  Dispatch strategy
                </div>
                <select
                  value={property.dispatch_strategy || 'all_per_round'}
                  onChange={handleStrategyChange}
                  style={{
                    width: '100%', padding: '6px 10px',
                    border: '1px solid var(--ayni-line-strong)',
                    background: 'var(--ayni-bg)', color: 'var(--ayni-ink)',
                    fontFamily: 'var(--ayni-f-body)', fontSize: '13px',
                  }}
                >
                  <option value="all_per_round">All Per Round</option>
                  <option value="priority_first">Priority First</option>
                </select>
                {strategySaved && (
                  <div style={{ marginTop: '6px', fontSize: '11px', color: 'var(--ayni-status-active)' }}>
                    Saved
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="ayni-seg">
            <button className={viewMode === 'table' ? 'on' : ''} onClick={() => setViewMode('table')}>Table</button>
            <button className={viewMode === 'calendar' ? 'on' : ''} onClick={() => setViewMode('calendar')}>Calendar</button>
          </div>
          <button className="ayni-btn" onClick={exportToCSV}>Export CSV</button>
          <button className="ayni-btn primary" onClick={() => setIsSimModalOpen(true)}>New</button>
        </div>
      </div>

      {/* Summary row */}
      <div className="ayni-detail-summary">
        <div className="ayni-si">
          <div className="ayni-n ayni-num">{activeCount}</div>
          <div className="ayni-l">Active</div>
        </div>
        <div className="ayni-divider"></div>
        <div className={`ayni-si ${pendingCount > 0 ? 'pending' : ''}`}>
          <div className="ayni-n ayni-num">{pendingCount}</div>
          <div className="ayni-l">Pending review</div>
        </div>
        <div className="ayni-divider"></div>
        <div className="ayni-si">
          <div className="ayni-n ayni-num">{pastCount}</div>
          <div className="ayni-l">Past</div>
        </div>
      </div>

      {/* Calendar View */}
      {viewMode === 'calendar' && (
        <CalendarMonthGrid
          reservations={reservations}
          getReservationStatus={getReservationStatus}
          parseDateLocal={parseDateLocal}
          checkInTime={property?.check_in_time || '15:00'}
          checkOutTime={property?.check_out_time || '11:00'}
          onReservationClick={(reservation) => {
            setViewMode('table');
            setExpandedRows(new Set([reservation.UID]));
          }}
          onEmptyDayClick={(dateStr) => {
            setSimForm(prev => ({ ...prev, check_in: dateStr }));
            setIsSimModalOpen(true);
          }}
        />
      )}

      {/* Pending Extension Banner */}
      {pendingExtensions.length > 0 && pendingExtensions.map((ext, idx) => (
        <div key={ext.new_uid || idx} style={{
          backgroundColor: 'var(--badge-orange-bg)',
          border: '1px solid #f97316',
          borderRadius: '10px',
          padding: '16px 20px',
          marginBottom: '16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '16px',
          flexWrap: 'wrap',
        }}>
          <div style={{ flex: 1, minWidth: '200px' }}>
            <div style={{ fontWeight: '600', fontSize: '14px', color: 'var(--badge-orange-text)', marginBottom: '4px' }}>
              Possible Extension Detected
            </div>
            <div style={{ fontSize: '13px', color: '#7c2d12' }}>
              <strong>{ext.assigned_cleaner}</strong> was assigned until {ext.old_check_out}.
              New reservation starts {ext.new_check_in} {'\u2192'} {ext.new_check_out}.
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
            <button
              onClick={() => handleResolveExtension(ext, 'skip_cleaning')}
              style={{
                padding: '8px 16px',
                backgroundColor: '#f0fdf4',
                color: '#166534',
                border: '1px solid #86efac',
                borderRadius: '8px',
                fontSize: '13px',
                fontWeight: '600',
                cursor: 'pointer',
              }}
            >
              Skip Cleaning
            </button>
            <button
              onClick={() => handleResolveExtension(ext, 'send_cleaner')}
              style={{
                padding: '8px 16px',
                backgroundColor: 'var(--color-primary)',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '13px',
                fontWeight: '600',
                cursor: 'pointer',
              }}
            >
              Send Aynista
            </button>
          </div>
        </div>
      ))}

      {/* Active & Future Reservations Table */}
      {viewMode === 'table' && (<>

      {/* Editorial tabs */}
      <div className="ayni-tabs">
        <button className={`ayni-tab tone-green ${activeTab === 'active' ? 'on' : ''}`} onClick={() => setActiveTab('active')}>
          Active <span className="ayni-count">{activeCount}</span>
        </button>
        <button className={`ayni-tab tone-amber ${activeTab === 'pending' ? 'on' : ''}`} onClick={() => setActiveTab('pending')}>
          Pending <span className="ayni-count">{pendingCount}</span>
        </button>
        <button className={`ayni-tab ${activeTab === 'past' ? 'on' : ''}`} onClick={() => setActiveTab('past')}>
          Past <span className="ayni-count">{pastCount}</span>
        </button>
      </div>

      <div ref={activeRef}></div>
      <div ref={pendingRef}></div>
      <div ref={pastRef}></div>

      {(() => {
        // Pick the reservations list to render based on active tab
        const rowsForTab = activeTab === 'active'
          ? currentAndUpcoming
          : activeTab === 'pending'
            ? pendingCleaning
            : pastReservations;

        // Date formatting helpers
        const fmt = (iso) => {
          if (!iso) return '—';
          const d = parseDateLocal(iso);
          return d.toLocaleDateString('en-US', { day: 'numeric', month: 'short' });
        };
        const yr = (iso) => iso ? parseDateLocal(iso).getFullYear() : '';
        const fmtCleaningTime = (t) => {
          if (!t) return '';
          const [h, m] = t.split(':').map(Number);
          const sfx = h >= 12 ? 'pm' : 'am';
          const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
          return m === 0 ? `${h12}${sfx}` : `${h12}:${String(m).padStart(2, '0')}${sfx}`;
        };
        const statusClassFor = (s) => {
          if (s === 'ACTIVE') return 'active';
          if (s === 'FUTURE') return 'quiet';
          if (s === 'BLOCKED') return 'alert';
          if (s === 'INACTIVE') return 'quiet';
          return 'quiet';
        };
        const statusLabelFor = (s) => {
          if (s === 'ACTIVE') return 'Active';
          if (s === 'FUTURE') return 'Future';
          if (s === 'BLOCKED') return 'Blocked';
          if (s === 'INACTIVE') return 'Past';
          return s;
        };

        return (
          <div className="ayni-card">
          <table className="ayni-table">
            <thead>
              <tr>
                <th>Check-in</th>
                <th>Check-out</th>
                <th>Cleaning</th>
                <th>Status</th>
                <th>Verification</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rowsForTab.map((r) => {
                const status = getReservationStatus(r['CHECK-IN'], r['CHECK-OUT'], r.reservation_type);
                const remark = getRemarks(r.assigned_cleaner);
                const origin = getReservationOrigin(r);
                const isExpanded = expandedRows.has(r.UID);
                const checkoutPassed = r['CHECK-OUT'] && new Date(r['CHECK-OUT'] + 'T00:00:00') <= new Date();
                const ver = verifications[r.UID];
                const vPending = ver && (ver.status === 'REVIEW_REQUIRED' || ver.pending_review);
                const vDone = ver && (ver.status === 'APPROVED' || ver.status === 'ADMIN_APPROVED');
                const vFlagged = ver && (ver.status === 'FLAGGED' || ver.status === 'SCORING_FAILED');
                const cleaningDate = r.cleaning_date || r['CHECK-OUT'];
                const checkOutTimeStr = property?.check_out_time || '';

                return (
                  <React.Fragment key={r.UID}>
                    <tr onClick={() => toggleExpand(r.UID)} style={{ cursor: 'pointer' }}>
                      <td>{fmt(r['CHECK-IN'])}</td>
                      <td>
                        <strong style={{ fontFamily: 'var(--ayni-f-display)', fontSize: 16, fontWeight: 450 }}>{fmt(r['CHECK-OUT'])}</strong>
                        <span style={{ color: 'var(--ayni-ink-4)', fontSize: 12, marginLeft: 4 }}>{yr(r['CHECK-OUT'])}</span>
                        {r.original_checkout && (
                          <span
                            title={`Originally ${r.original_checkout}`}
                            style={{
                              marginLeft: '6px', padding: '2px 6px',
                              border: '1px solid var(--ayni-status-pending)',
                              color: 'var(--ayni-status-pending)',
                              fontSize: '10px', fontWeight: 500, letterSpacing: '0.04em', textTransform: 'uppercase',
                            }}
                          >
                            Extended
                          </span>
                        )}
                      </td>
                      <td className="ayni-num" style={{ color: 'var(--ayni-ink-2)' }}>
                        {fmt(cleaningDate)}{checkOutTimeStr ? ` · ${checkOutTimeStr}` : ''}
                        {r.cleaning_rescheduled && (
                          <span title="Rescheduled" style={{ marginLeft: '6px', color: 'var(--ayni-status-pending)' }}>&#8635;</span>
                        )}
                        {r.cleaning_time && (
                          <span style={{ marginLeft: '6px', color: 'var(--ayni-status-pending)', fontSize: '11px' }}>
                            {fmtCleaningTime(r.cleaning_time)}
                          </span>
                        )}
                      </td>
                      <td>
                        <span className={`ayni-status ${statusClassFor(status)}`}>{statusLabelFor(status)}</span>
                      </td>
                      <td>
                        {!checkoutPassed && activeTab === 'active' ? (
                          <span className="ayni-vcell">—</span>
                        ) : vPending ? (
                          <span className="ayni-vcell pending">Review needed</span>
                        ) : vDone ? (
                          <span className="ayni-vcell done">Verified</span>
                        ) : vFlagged ? (
                          <span className="ayni-vcell pending">Flagged</span>
                        ) : ver && ver.status === 'IN_PROGRESS' ? (
                          <span className="ayni-vcell">In progress</span>
                        ) : ver ? (
                          <span className="ayni-vcell">{ver.status}</span>
                        ) : (
                          <span className="ayni-vcell">—</span>
                        )}
                      </td>
                      <td style={{ color: 'var(--ayni-ink-4)' }}>{isExpanded ? '⌄' : '›'}</td>
                    </tr>
                    {isExpanded && (
                      <tr>
                        <td colSpan={6} style={{ background: 'var(--ayni-elevated)', padding: 0 }}>
                          <div style={{ padding: '16px 20px', display: 'flex', flexWrap: 'wrap', gap: '18px', alignItems: 'flex-start' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <span style={{ fontSize: '10px', letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--ayni-ink-3)' }}>Customer ID</span>
                              <span title={r.UID} style={{ fontFamily: 'var(--ayni-f-mono)', fontSize: '12px', color: 'var(--ayni-ink-2)' }}>{truncateUid(r.UID)}</span>
                              <button
                                title="Copy full ID"
                                onClick={(e) => { e.stopPropagation(); handleCopyUid(r.UID); }}
                                style={{
                                  background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                                  color: copiedUid === r.UID ? 'var(--ayni-status-active)' : 'var(--ayni-ink-4)',
                                  fontSize: '12px',
                                }}
                              >
                                {copiedUid === r.UID ? '✓' : '⎘'}
                              </button>
                            </div>

                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <span style={{ fontSize: '10px', letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--ayni-ink-3)' }}>Origin</span>
                              <span style={{ fontSize: '12px', color: 'var(--ayni-ink)' }}>{origin}</span>
                            </div>

                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <span style={{ fontSize: '10px', letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--ayni-ink-3)' }}>Aynista</span>
                              <span style={{ fontSize: '12px', color: r.assigned_cleaner === 'Unassigned' ? 'var(--ayni-ink-4)' : 'var(--ayni-ink)' }}>
                                {r.assigned_cleaner || 'N/A'}
                              </span>
                              {r.assigned_cleaner === 'Unassigned' && r.dispatch_attempts && (
                                <span style={{ fontSize: '11px', color: 'var(--ayni-status-pending)' }}>
                                  {r.dispatch_attempts.total_attempts} attempt{r.dispatch_attempts.total_attempts !== 1 ? 's' : ''}, {r.dispatch_attempts.cleaners_contacted} cleaner{r.dispatch_attempts.cleaners_contacted !== 1 ? 's' : ''}
                                </span>
                              )}
                            </div>

                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <span style={{ fontSize: '10px', letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--ayni-ink-3)' }}>Remarks</span>
                              {r.pending_update ? (
                                <span style={{ fontSize: '12px', color: 'var(--ayni-status-pending)' }}>Awaiting update confirmation</span>
                              ) : remark ? (
                                <span style={{ fontSize: '12px', color: 'var(--ayni-status-alert)' }}>{remark.tooltip}</span>
                              ) : (
                                <span style={{ fontSize: '12px', color: 'var(--ayni-ink-4)' }}>None</span>
                              )}
                            </div>

                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                              <span style={{ fontSize: '10px', letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--ayni-ink-3)' }}>Actions</span>
                              <button
                                className="ayni-btn"
                                onClick={(e) => { e.stopPropagation(); handleReDispatchAll(r); }}
                                style={{ padding: '5px 12px', fontSize: '12px' }}
                              >
                                Re-Dispatch All
                              </button>
                              <button
                                className="ayni-btn"
                                onClick={(e) => { e.stopPropagation(); openCleanerPicker(r); }}
                                style={{ padding: '5px 12px', fontSize: '12px' }}
                              >
                                Select Aynistas
                              </button>
                            </div>

                            {r.assigned_cleaner === 'Unassigned' && r.dispatch_attempts && r.dispatch_attempts.details && (
                              <div style={{ width: '100%', display: 'flex', alignItems: 'flex-start', gap: '8px', flexWrap: 'wrap' }}>
                                <span style={{ fontSize: '10px', letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--ayni-ink-3)', paddingTop: '3px' }}>Dispatch Attempts</span>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                  {r.dispatch_attempts.details.map((d, i) => (
                                    <span key={i} style={{
                                      fontSize: '11px',
                                      padding: '3px 8px',
                                      border: `1px solid ${d.last_response === 'NO' ? 'var(--ayni-status-alert)' : d.last_response === 'NO_REPLY' ? 'var(--ayni-status-pending)' : 'var(--ayni-status-active)'}`,
                                      color: d.last_response === 'NO' ? 'var(--ayni-status-alert)' : d.last_response === 'NO_REPLY' ? 'var(--ayni-status-pending)' : 'var(--ayni-status-active)',
                                    }}>
                                      {d.name}: {d.attempts}x — {d.last_response}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}

                            <div style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                              <span style={{ fontSize: '10px', letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--ayni-ink-3)' }}>Test Checkout</span>
                              <input
                                type="date"
                                value={testDates[r.UID] || ''}
                                onChange={(e) => handleTestDateChange(r.UID, e.target.value)}
                                onClick={(e) => e.stopPropagation()}
                                style={{
                                  padding: '5px 8px',
                                  border: '1px solid var(--ayni-line-strong)',
                                  background: 'var(--ayni-bg)',
                                  color: 'var(--ayni-ink)',
                                  fontFamily: 'var(--ayni-f-body)',
                                  fontSize: '12px',
                                }}
                              />
                              <button
                                className="ayni-btn primary"
                                onClick={(e) => { e.stopPropagation(); handleLaunchTest(r.UID); }}
                                style={{ padding: '5px 12px', fontSize: '12px' }}
                              >
                                Launch Test
                              </button>
                              {origin === 'Simulated' && (
                                <button
                                  className="ayni-btn"
                                  onClick={(e) => { e.stopPropagation(); handleDeleteSim(r.UID); }}
                                  style={{ padding: '5px 12px', fontSize: '12px', marginLeft: 'auto', color: 'var(--ayni-status-alert)', borderColor: 'var(--ayni-status-alert)' }}
                                >
                                  Delete Simulation
                                </button>
                              )}
                            </div>

                            {verifications[r.UID] && r['CHECK-OUT'] && new Date(r['CHECK-OUT'] + 'T00:00:00') <= new Date() && (
                              <div style={{ width: '100%' }}>
                                <VerificationPanel
                                  verification={verifications[r.UID]}
                                  onRescore={async (updated) => {
                                    if (updated && updated.id) {
                                      setVerifications(prev => ({ ...prev, [r.UID]: updated }));
                                    } else {
                                      try {
                                        const fresh = await getVerification(propertyId, r.UID);
                                        setVerifications(prev => ({ ...prev, [r.UID]: fresh }));
                                      } catch (_) { /* ignore */ }
                                    }
                                  }}
                                />
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
              {rowsForTab.length === 0 && (
                <tr>
                  <td colSpan={6} style={{ padding: '32px 0', textAlign: 'center', color: 'var(--ayni-ink-3)', fontStyle: 'italic' }}>
                    No {activeTab} reservations.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          </div>
        );
      })()}
      </>)}

      {/* Simulate New Reservation Modal */}
      {isSimModalOpen && (
        <div
          onClick={() => setIsSimModalOpen(false)}
          style={styles.modalOverlay}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={styles.modalContent}
          >
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>Simulate New Reservation</h3>
              <button onClick={() => setIsSimModalOpen(false)} style={styles.modalClose}>&times;</button>
            </div>

            <form onSubmit={handleSimCreate} style={{ display: 'grid', gap: '16px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <label style={styles.formLabel}>Check-in</label>
                  <input
                    type="date" required value={simForm.check_in}
                    onChange={(e) => setSimForm({ ...simForm, check_in: e.target.value })}
                    style={styles.formInput}
                  />
                </div>
                <div>
                  <label style={styles.formLabel}>Check-out</label>
                  <input
                    type="date" required value={simForm.check_out}
                    onChange={(e) => setSimForm({ ...simForm, check_out: e.target.value })}
                    style={styles.formInput}
                  />
                </div>
              </div>

              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', color: 'var(--text-secondary)' }}>
                <input
                  type="checkbox" checked={simForm.dispatch}
                  onChange={(e) => setSimForm({ ...simForm, dispatch: e.target.checked })}
                />
                Dispatch cleaners now
              </label>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '4px' }}>
                <button type="button" onClick={() => setIsSimModalOpen(false)} style={styles.secondaryButton}>Cancel</button>
                <button type="submit" style={styles.primaryButton}>Create</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Cleaner Picker Modal */}
      {isCleanerPickerOpen && (
        <div style={{ ...styles.modalOverlay, zIndex: 2000 }}>
          <div style={{
            ...styles.modalContent,
            maxWidth: '600px',
            width: '90%',
            maxHeight: '80vh',
            overflow: 'auto',
          }}>
            <div style={styles.modalHeader}>
              <div>
                <h3 style={styles.modalTitle}>Select Aynistas to Contact</h3>
                <p style={{ color: 'var(--text-muted)', fontSize: '14px', margin: '8px 0 0 0' }}>
                  Property: <strong style={{ color: 'var(--text-primary)' }}>{propertyName}</strong> &middot; Check-out: <strong style={{ color: 'var(--text-primary)' }}>{currentReservation?.['CHECK-OUT']}</strong>
                </p>
              </div>
              <button onClick={() => setIsCleanerPickerOpen(false)} style={styles.modalClose}>&times;</button>
            </div>

            <div style={{ marginBottom: '20px', maxHeight: '400px', overflowY: 'auto' }}>
              {cleanersForPicker.map(cleaner => {
                const statusStyles = {
                  ACCEPTED: { bg: 'var(--badge-green-bg)', color: 'var(--badge-green-text)', text: 'Accepted', disabled: true },
                  DECLINED: { bg: 'var(--badge-red-bg)', color: 'var(--badge-red-text)', text: 'Declined', disabled: false },
                  NO_REPLY: { bg: 'var(--badge-orange-bg)', color: 'var(--badge-orange-text)', text: 'No Reply', disabled: false },
                  NOT_CONTACTED: { bg: 'var(--badge-gray-bg)', color: 'var(--badge-gray-text)', text: 'Not Contacted', disabled: false }
                };
                const style = statusStyles[cleaner.status];

                return (
                  <label
                    key={cleaner.cleaner_id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '12px',
                      marginBottom: '8px',
                      border: '1px solid var(--border-default)',
                      borderRadius: '8px',
                      cursor: style.disabled ? 'not-allowed' : 'pointer',
                      backgroundColor: selectedCleanerIds.includes(cleaner.cleaner_id) ? '#eef2ff' : 'var(--bg-card)',
                      opacity: style.disabled ? 0.6 : 1,
                      transition: 'background-color 0.2s',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={selectedCleanerIds.includes(cleaner.cleaner_id)}
                      onChange={() => toggleCleanerSelection(cleaner.cleaner_id)}
                      disabled={style.disabled}
                      style={{ marginRight: '12px', cursor: style.disabled ? 'not-allowed' : 'pointer' }}
                    />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: '500', marginBottom: '4px', color: 'var(--text-primary)' }}>
                        {cleaner.name} <span style={{ color: 'var(--text-muted)', fontWeight: '400', fontSize: '13px' }}>(Priority {cleaner.priority})</span>
                      </div>
                      <div style={{ fontSize: '12px', color: 'var(--text-faint)' }}>{cleaner.phone}</div>
                    </div>
                    <span style={{
                      backgroundColor: style.bg,
                      color: style.color,
                      padding: '4px 12px',
                      borderRadius: '20px',
                      fontSize: '11px',
                      fontWeight: '600',
                    }}>
                      {style.text}
                    </span>
                  </label>
                );
              })}
            </div>

            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              paddingTop: '16px',
              borderTop: '1px solid var(--border-light)',
            }}>
              <div style={{ fontSize: '14px', color: 'var(--text-muted)' }}>
                {selectedCleanerIds.length} cleaner(s) selected
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  onClick={() => setIsCleanerPickerOpen(false)}
                  style={styles.secondaryButton}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSelectiveDispatch}
                  disabled={selectedCleanerIds.length === 0}
                  style={{
                    ...styles.primaryButton,
                    backgroundColor: selectedCleanerIds.length === 0 ? 'var(--text-placeholder)' : 'var(--color-primary)',
                    cursor: selectedCleanerIds.length === 0 ? 'not-allowed' : 'pointer',
                  }}
                >
                  Dispatch to Selected ({selectedCleanerIds.length})
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PropertyDetails;
